import React, { useState, useEffect } from "react";
import { useNotify, useRedirect } from "react-admin";
import ResimUpload from "../ResimUpload";
import { API_BASE } from '../config';

const ALLOWED = ["tr", "en", "de"];
const PLACEHOLDER = "Bu dilde içerik mevcut değildir.";

/* --- UI METİNLERİ (etiketler, butonlar, bildirimler) --- */
const T = {
  tr: {
    pageTitle: "Haber Ekle",
    title: "Başlık",
    image: "Resim",
    description: "Açıklama",
    link: "Link",
    contentLang: "İçerik Dili",
    status: "Durum",
    active: "Aktif",
    passive: "Pasif",
    save: "Kaydet",
    uploadImage: "📤 Görsel Yükle",
    savedOk: "✅ Duyuru başarıyla eklendi",
    savedErr: "❌ Duyuru eklenirken hata oluştu",
    langNames: { tr: "Türkçe", en: "İngilizce", de: "Almanca" },
    placeholders: {
      title: { tr: "Türkçe başlık girin", en: "Enter English title", de: "Almanca başlık girin" },
      desc: {
        tr: "Türkçe açıklama yazın",
        en: "Write description in English",
        de: "Almanca açıklama yazın",
      },
    },
  },
  en: {
    pageTitle: "Add News",
    title: "Title",
    image: "Image",
    description: "Description",
    link: "Link",
    contentLang: "Content Language",
    status: "Status",
    active: "Active",
    passive: "Inactive",
    save: "Save",
    uploadImage: "📤 Upload Image",
    savedOk: "✅ Announcement added successfully",
    savedErr: "❌ Failed to add announcement",
    langNames: { tr: "Turkish", en: "English", de: "German" },
    placeholders: {
      title: { tr: "Enter Turkish title", en: "Enter English title", de: "Enter German title" },
      desc: {
        tr: "Write description in Turkish",
        en: "Write description in English",
        de: "Write description in German",
      },
    },
  },
  de: {
    pageTitle: "Nachricht hinzufügen",
    title: "Titel",
    image: "Bild",
    description: "Beschreibung",
    link: "Link",
    contentLang: "Inhaltssprache",
    status: "Status",
    active: "Aktiv",
    passive: "Inaktiv",
    save: "Speichern",
    uploadImage: "📤 Bild hochladen",
    savedOk: "✅ Mitteilung erfolgreich hinzugefügt",
    savedErr: "❌ Mitteilung konnte nicht hinzugefügt werden",
    langNames: { tr: "Türkisch", en: "Englisch", de: "Deutsch" },
    placeholders: {
      title: { tr: "Türkischen Titel eingeben", en: "Englischen Titel eingeben", de: "Deutschen Titel eingeben" },
      desc: {
        tr: "Beschreibung auf Türkisch schreiben",
        en: "Beschreibung auf Englisch schreiben",
        de: "Beschreibung auf Deutsch schreiben",
      },
    },
  },
};

const DuyuruCard = () => {
  // UI dili (Ayarlar > arayüz dili)
  const [uiLang, setUiLang] = useState(
    () => (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase()
  );
  const TT = T[ALLOWED.includes(uiLang) ? uiLang : "tr"];

  const [formData, setFormData] = useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",
    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",
    link: "https://",
    resim: "",
    durum: "Aktif",
    dil: "tr", // içerik dili
  });

  const [aktifDiller, setAktifDiller] = useState(["tr", "en", "de"]);
  const notify = useNotify();
  const redirect = useRedirect();

  // ✅ Ayarları oku: UI dili (app_lang) + kurum ayarlarından aktif diller & varsayılan
  useEffect(() => {
    const applySettings = async () => {
      const appLang = (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase();
      setUiLang(ALLOWED.includes(appLang) ? appLang : "tr");

      let nextDil = ALLOWED.includes(appLang) ? appLang : "tr"; // öncelik UI dili
      let nextAktif = ["tr", "en", "de"];
      let apiDil = null;

      try {
        const token = localStorage.getItem("token");
        if (token) {
          const res = await fetch(`${API_BASE}/kurum-ayarlar`, {
            headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const json = await res.json();
            const d = json?.data || null;

            if (d) {
              // aktif diller
              let aktif = d.aktif_diller;
              if (typeof aktif === "string") {
                try { aktif = JSON.parse(aktif); } catch { aktif = ["tr", "en", "de"]; }
              }
              if (Array.isArray(aktif) && aktif.length) {
                nextAktif = aktif.filter((x) => ALLOWED.includes(x));
              }
              // varsayılan içerik dili
              const candidate = (d.dil || "").toLowerCase();
              apiDil = ALLOWED.includes(candidate) ? candidate : null;
            }
          }
        }
      } catch { /* sessiz geç */ }

      // içerik dilini seç
      if (nextAktif.includes(appLang)) {
        nextDil = appLang;
      } else if (apiDil && nextAktif.includes(apiDil)) {
        nextDil = apiDil;
      } else {
        nextDil = nextAktif[0] || "tr";
      }

      setAktifDiller(nextAktif);
      setFormData((prev) => ({ ...prev, dil: nextDil }));
    };

    applySettings();

    const sync = () => applySettings();
    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("app_settings_change", sync);
    window.addEventListener("app_lang_change", sync);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("app_settings_change", sync);
      window.removeEventListener("app_lang_change", sync);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const aktif = formData.durum === "Aktif";
    const tarih = new Date().toISOString().slice(0, 10);
    const linkRaw = (formData.link || '').trim();
    const finalLink = linkRaw && linkRaw !== 'https://' ? linkRaw : '';

    const duyuru = {
      baslik_tr: formData.dil === "tr" ? formData.baslik_tr : PLACEHOLDER,
      baslik_en: formData.dil === "en" ? formData.baslik_en : PLACEHOLDER,
      baslik_de: formData.dil === "de" ? formData.baslik_de : PLACEHOLDER,
      aciklama_tr: formData.dil === "tr" ? formData.aciklama_tr : PLACEHOLDER,
      aciklama_en: formData.dil === "en" ? formData.aciklama_en : PLACEHOLDER,
      aciklama_de: formData.dil === "de" ? formData.aciklama_de : PLACEHOLDER,
      resim: formData.resim,
      link: finalLink,
      aktif,
      tarih,
      dil: formData.dil,
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        notify(TT.savedErr, { type: "error" });
        return;
      }

      const res = await fetch(`${API_BASE}/duyurular`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(duyuru),
      });

      if (res.ok) {
        notify(TT.savedOk, { type: "success" });
        redirect("/duyurular");
      } else {
        notify(TT.savedErr, { type: "error" });
      }
    } catch (err) {
      console.error("Sunucu hatası:", err);
      notify(TT.savedErr, { type: "error" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Güvence: seçili dil aktif listede değilse uygun birine düş
  useEffect(() => {
    if (!aktifDiller.includes(formData.dil)) {
      setFormData((p) => ({ ...p, dil: aktifDiller[0] || "tr" }));
    }
  }, [aktifDiller, formData.dil]);

  // Placeholder yardımcıları (içerik diline göre)
  const phTitle = T[uiLang].placeholders.title[formData.dil];
  const phDesc = T[uiLang].placeholders.desc[formData.dil];

  return (
    <div className="p-6 w/full bg-white rounded shadow ml-0">
      <h2 className="text-xl font-semibold mb-4">{TT.pageTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Başlık */}
        <div>
          <label className="block font-medium mb-1">{TT.title}</label>
          <input
            name={`baslik_${formData.dil}`}
            value={formData[`baslik_${formData.dil}`] || ""}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            placeholder={phTitle}
            required
          />
        </div>

        {/* Resim */}
        <div>
          <label className="block font-medium mb-1">{TT.image}</label>
          <ResimUpload
            onUpload={(url) => setFormData({ ...formData, resim: url })}
            defaultPreview={formData.resim}
            label={TT.uploadImage}
          />
        </div>

        {/* Açıklama */}
        <div>
          <label className="block font-medium mb-1">{TT.description}</label>
          <textarea
            name={`aciklama_${formData.dil}`}
            value={formData[`aciklama_${formData.dil}`] || ""}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            rows={4}
            placeholder={phDesc}
            required
          />
        </div>

        {/* Link */}
        <div>
          <label className="block font-medium mb-1">{TT.link}</label>
          <input
            name="link"
            value={formData.link}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        {/* İçerik Dili */}
        <div>
          <label className="block font-medium mb-1">{TT.contentLang}</label>
          <select
            name="dil"
            value={formData.dil}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          >
            {aktifDiller.includes("tr") && <option value="tr">{TT.langNames.tr}</option>}
            {aktifDiller.includes("en") && <option value="en">{TT.langNames.en}</option>}
            {aktifDiller.includes("de") && <option value="de">{TT.langNames.de}</option>}
          </select>
        </div>

        {/* Durum */}
        <div>
          <label className="block font-medium mb-1">{TT.status}</label>
          <select
            name="durum"
            value={formData.durum}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          >
            <option value="Aktif">{TT.active}</option>
            <option value="Pasif">{TT.passive}</option>
          </select>
        </div>

        {/* Kaydet */}
        <div>
          <button type="submit" className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600">
            {TT.save}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DuyuruCard;
